// JsonTree — devtools-style collapsible JSON viewer.
//
// Operator clicks an event row → lineage / event-detail drawer used to
// dump JSON.stringify(payload).slice(0, 600), which truncated 2-5 KB
// Pipedrive payloads mid-string. This component renders any JS value as
// an indented tree where every object / array node can be collapsed or
// expanded; everything is reachable, large nodes don't dominate the
// viewport, and operator scans by structure.
//
// Generic — no per-source logic. Used by LineageDrawer + any future
// "view full event" surface.

import * as React from 'react';

type Kind = 'null' | 'string' | 'number' | 'boolean' | 'array' | 'object';

const TYPE_COLOUR: Record<Kind, string> = {
  null:    'oklch(0.62 0.02 250)',
  string:  'oklch(0.42 0.14 145)',
  number:  'oklch(0.40 0.16 240)',
  boolean: 'oklch(0.42 0.18 295)',
  array:   'oklch(0.45 0.16 65)',
  object:  'oklch(0.40 0.14 235)',
};

function kindOf(v: unknown): Kind {
  if (v === null || v === undefined) return 'null';
  if (Array.isArray(v)) return 'array';
  switch (typeof v) {
    case 'string': return 'string';
    case 'number': return 'number';
    case 'boolean': return 'boolean';
    default: return 'object';
  }
}

/** Auto-pick initial expansion depth based on serialised size, so a
 *  small payload renders fully open and a 50KB payload doesn't fill
 *  the viewport. Pure heuristic on byte size — generic. */
function defaultExpandDepth(value: unknown): number {
  let size: number;
  try { size = JSON.stringify(value).length; }
  catch { return 1; }
  if (size < 2_000) return 99;       // tiny → expand fully
  if (size < 8_000) return 3;        // small-medium → first 3 levels
  if (size < 32_000) return 2;       // medium → first 2 levels
  return 1;                          // large → top-level only
}

export interface JsonTreeProps {
  value: unknown;
  /** How many levels to expand initially. If omitted, auto-picked from
   *  serialised size of `value`. Operator can still toggle deeper. */
  initialExpandDepth?: number;
  /** Hard cap on render depth (safety against pathological circular
   *  structures; JSON.stringify already protects but this caps DOM). */
  maxDepth?: number;
  /** Compact rendering — smaller padding, smaller font. */
  compact?: boolean;
}

export function JsonTree({
  value, initialExpandDepth, maxDepth = 12, compact,
}: JsonTreeProps): React.ReactElement {
  const initial = initialExpandDepth ?? defaultExpandDepth(value);
  return (
    <div style={{
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: compact ? 11 : 12,
      lineHeight: 1.55,
      color: 'var(--ink-2)',
      whiteSpace: 'pre',
    }}>
      <JsonNode
        value={value}
        depth={0}
        initialExpandDepth={initial}
        maxDepth={maxDepth}
        path=""
      />
    </div>
  );
}

/** One node in the tree. Recursively renders children. */
function JsonNode({
  value, depth, initialExpandDepth, maxDepth, path,
}: {
  value: unknown;
  depth: number;
  initialExpandDepth: number;
  maxDepth: number;
  path: string;
}): React.ReactElement {
  const kind = kindOf(value);
  const [expanded, setExpanded] = React.useState<boolean>(depth < initialExpandDepth);

  // Safety: don't render past maxDepth.
  if (depth > maxDepth) {
    return <span style={{ color: 'var(--ink-3)' }}>…</span>;
  }

  // ── scalars ────────────────────────────────────────────────────
  if (kind === 'null') return <span style={{ color: TYPE_COLOUR.null, fontStyle: 'italic' }}>null</span>;
  if (kind === 'string') {
    const s = value as string;
    return <span style={{ color: TYPE_COLOUR.string }}>&quot;{escapeStringForDisplay(s)}&quot;</span>;
  }
  if (kind === 'number') return <span style={{ color: TYPE_COLOUR.number }}>{String(value)}</span>;
  if (kind === 'boolean') return <span style={{ color: TYPE_COLOUR.boolean }}>{String(value)}</span>;

  // ── object / array ──────────────────────────────────────────────
  const isArray = kind === 'array';
  const entries: Array<[string, unknown]> = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);
  const count = entries.length;
  const openBrace = isArray ? '[' : '{';
  const closeBrace = isArray ? ']' : '}';
  const tone = isArray ? TYPE_COLOUR.array : TYPE_COLOUR.object;

  const summaryLabel = isArray
    ? `${count} item${count === 1 ? '' : 's'}`
    : `${count} key${count === 1 ? '' : 's'}`;

  // Empty container — render inline, no toggle.
  if (count === 0) {
    return <span style={{ color: tone }}>{openBrace}{closeBrace}</span>;
  }

  // Toggle button label: `{ 5 keys ▾ }` collapsed, `{ ▴` expanded.
  return (
    <>
      <span
        onClick={(e) => { e.stopPropagation(); setExpanded(x => !x); }}
        title={expanded ? 'Collapse' : 'Expand'}
        style={{
          color: tone, cursor: 'pointer', userSelect: 'none',
        }}
      >
        {openBrace}
        {!expanded && (
          <>
            <span style={{ color: 'var(--ink-3)', fontStyle: 'italic', margin: '0 6px' }}>{summaryLabel}</span>
            {closeBrace}
            <span style={{ marginLeft: 4, opacity: 0.6 }}>▾</span>
          </>
        )}
        {expanded && <span style={{ marginLeft: 4, opacity: 0.6 }}>▴</span>}
      </span>
      {expanded && (
        <>
          {'\n'}
          {entries.map(([key, child], i) => {
            const isLast = i === entries.length - 1;
            const childKind = kindOf(child);
            const childPath = path ? `${path}.${key}` : key;
            return (
              <React.Fragment key={key}>
                <span style={{ paddingLeft: (depth + 1) * 14 }}>
                  {!isArray && (
                    <>
                      <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{escapeKeyForDisplay(key)}</span>
                      <span style={{ color: 'var(--ink-3)' }}>: </span>
                    </>
                  )}
                  <JsonNode
                    value={child}
                    depth={depth + 1}
                    initialExpandDepth={initialExpandDepth}
                    maxDepth={maxDepth}
                    path={childPath}
                  />
                  {childKind !== 'object' && childKind !== 'array' && !isLast && (
                    <span style={{ color: 'var(--ink-3)' }}>,</span>
                  )}
                </span>
                {'\n'}
              </React.Fragment>
            );
          })}
          <span style={{ paddingLeft: depth * 14, color: tone }}>{closeBrace}</span>
        </>
      )}
    </>
  );
}

/** Escape control chars + quote in strings for display. */
function escapeStringForDisplay(s: string): string {
  // Only escape what would break the display; keep unicode as-is.
  return s
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t');
}

/** Object keys are rendered without quotes by default for readability.
 *  If a key has a space or special char, fall back to quoted form. */
function escapeKeyForDisplay(k: string): string {
  if (/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(k)) return k;
  return `"${escapeStringForDisplay(k)}"`;
}

// ── Convenience: header with expand-all / collapse-all controls ─────

export interface JsonTreeWithControlsProps extends JsonTreeProps {
  /** Optional title shown above the tree. */
  title?: React.ReactNode;
}

/** Same as JsonTree but with a small toolbar: total size, expand-all,
 *  collapse-all, copy-to-clipboard. Use when this is the primary
 *  surface of a drawer / modal (not inline inside another grid). */
export function JsonTreeWithControls(props: JsonTreeWithControlsProps): React.ReactElement {
  const { value, title, ...rest } = props;
  const [forcedDepth, setForcedDepth] = React.useState<number | undefined>(undefined);
  // Key bump forces remount of JsonTree so per-node expanded state
  // resets when operator clicks expand-all / collapse-all.
  const [bumpKey, setBumpKey] = React.useState(0);
  const size = React.useMemo(() => {
    try { return JSON.stringify(value).length; } catch { return -1; }
  }, [value]);

  function copy(): void {
    try {
      void navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    } catch { /* clipboard unavailable */ }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontFamily: 'var(--mono, ui-monospace)',
        fontSize: 11, color: 'var(--ink-3)',
      }}>
        {title && <span style={{ color: 'var(--ink-2)' }}>{title}</span>}
        {size >= 0 && <span>{fmtBytes(size)}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => { setForcedDepth(99); setBumpKey(k => k + 1); }}
          style={controlBtnStyle} title="Expand all nodes">
          expand all
        </button>
        <button type="button" onClick={() => { setForcedDepth(0); setBumpKey(k => k + 1); }}
          style={controlBtnStyle} title="Collapse all nodes">
          collapse all
        </button>
        <button type="button" onClick={copy} style={controlBtnStyle} title="Copy raw JSON to clipboard">
          copy
        </button>
      </div>
      <JsonTree key={bumpKey} value={value} initialExpandDepth={forcedDepth} {...rest} />
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

const controlBtnStyle: React.CSSProperties = {
  padding: '2px 8px',
  fontSize: 11, fontFamily: 'var(--mono, ui-monospace)',
  background: 'var(--bg, transparent)',
  border: '1px solid var(--line, #e0e0e0)',
  borderRadius: 3, color: 'var(--ink-2)',
  cursor: 'pointer',
};
